import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { UserRole } from '../redux/authSlice';

interface RoleGuardProps {
  requiredRole: UserRole;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * RoleGuard — conditionally renders children based on the authenticated user's role.
 *
 * - Renders `children` when the user's role matches `requiredRole` (and admin accounts are approved).
 * - Renders `fallback` if provided and access is denied.
 * - Returns `null` otherwise (no placeholder, no stub UI).
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  requiredRole,
  fallback,
  children,
}) => {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

  if (!isAuthenticated || !user) {
    return null;
  }

  // Access rules:
  // - Admins: only when accountStatus is 'approved'
  // - Customers: allow access regardless of accountStatus (KYC gating is handled elsewhere)
  const hasAccess =
    user.role === requiredRole &&
    (requiredRole === 'admin' ? user.accountStatus === 'approved' : true);

  if (hasAccess) {
    return <>{children}</>;
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  return null;
};

export default RoleGuard;
